import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Layout from "../hocs/Layout";
import { connect } from "react-redux";
import { Container, Row, Col, Spinner, Badge } from "react-bootstrap";
import {
  FaBolt,
  FaFire,
  FaClipboardList,
  FaUserCheck,
  FaTimesCircle,
  FaBan
} from "react-icons/fa";

import StatCard from "../components/dashboard/StatCard";
import QuickActionCard from "../components/dashboard/QuickActionCard";
import { get_ofertas_erp } from "../services/ofertas";
import { resourceService } from "../services/resourceService";
import { getFileIcon, getFileExtension } from "../utils/fileUtils";
import { stageOptions } from "../utils/ofertasUtils";
import "./Dashboard.css";

const Home = ({ isAuthenticated, user, isSuperAdmin }) => {
  const isCallAgent = Boolean(user?.is_call_agent);
  const isRestrictedCallAgent = isCallAgent && !isSuperAdmin;
  const [statsValues, setStatsValues] = useState({
    solicitudOferta: null,
    contratoEnviado: null,
    rechazadaSolAtr: null,
    ofertaRechazada: null,
  });
  const [resources, setResources] = useState([]);
  const [loadingResources, setLoadingResources] = useState(true);

  const getStageOption = (value) => stageOptions.find((option) => option.value === value);

  useEffect(() => {
    if (isAuthenticated !== true || !user) return;

    const fetchStats = async () => {
      // Avoid fetching if already loaded
      if (statsValues.solicitudOferta !== null) return;
      if (!user?.erp_user_id) return;

      const stageSolicitud = getStageOption(9);
      const stageContrato = getStageOption(13);
      const stageRechazadaAtr = getStageOption(18);
      const stageOfertaRechazada = getStageOption(31);

      try {
        const solicitudOferta = await get_ofertas_erp(
          user.erp_user_id,
          1,
          "",
          stageSolicitud ? { etapa: [stageSolicitud] } : {}
        );

        if (isRestrictedCallAgent) {
          setStatsValues({
            solicitudOferta: solicitudOferta?.total_records ?? 0,
            contratoEnviado: 0,
            rechazadaSolAtr: 0,
            ofertaRechazada: 0,
          });
          return;
        }

        const contratoEnviado = await get_ofertas_erp(
          user.erp_user_id,
          1,
          "",
          stageContrato ? { etapa: [stageContrato] } : {}
        );
        const rechazadaSolAtr = await get_ofertas_erp(
          user.erp_user_id,
          1,
          "",
          stageRechazadaAtr ? { etapa: [stageRechazadaAtr] } : {}
        );
        const ofertaRechazada = await get_ofertas_erp(
          user.erp_user_id,
          1,
          "",
          stageOfertaRechazada ? { etapa: [stageOfertaRechazada] } : {}
        );

        setStatsValues({
          solicitudOferta: solicitudOferta?.total_records ?? 0,
          contratoEnviado: contratoEnviado?.total_records ?? 0,
          rechazadaSolAtr: rechazadaSolAtr?.total_records ?? 0,
          ofertaRechazada: ofertaRechazada?.total_records ?? 0,
        });
      } catch (err) {
        setStatsValues({
          solicitudOferta: 0,
          contratoEnviado: 0,
          rechazadaSolAtr: 0,
          ofertaRechazada: 0,
        });
      }
    };

    const fetchResources = async () => {
      // if (isRestrictedCallAgent) {
      //   setLoadingResources(false);
      //   return;
      // }

      // Avoid fetching if already loaded
      if (resources.length > 0) return;

      try {
        setLoadingResources(true);
        const data = await resourceService.getResources();
        // Exclude legal category (goes in Footer) and release_notes (go in Novedades)
        const filteredData = data.filter(res => res.category !== 'legal' && res.category !== 'release_notes');
        // Show latest 6 resources
        setResources(filteredData.slice(0, 6));
      } catch (error) {
        console.error("Error loading resources for dashboard:", error);
      } finally {
        setLoadingResources(false);
      }
    };

    fetchResources();
    fetchStats();
  }, [isAuthenticated, user, statsValues.solicitudOferta, resources.length, isRestrictedCallAgent]);

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  const stats = isRestrictedCallAgent //si es CallAgent (no superadmin) només mostra "Solicitud Oferta"
    ? [
      {
        title: "Solicitud Oferta",
        value: statsValues.solicitudOferta ?? "—",
        icon: FaClipboardList,
        trend: null,
        color: "primary",
        to: "/contratos/ofertas?etapa=9"
      },
    ]
    : [
      {
        title: "Solicitud Oferta",
        value: statsValues.solicitudOferta ?? "—",
        icon: FaClipboardList,
        trend: null,
        color: "primary",
        to: "/contratos/ofertas?etapa=9"
      },
      {
        title: "Contrato Enviado",
        value: statsValues.contratoEnviado ?? "—",
        icon: FaUserCheck,
        trend: null,
        color: "info",
        to: "/contratos/ofertas?etapa=13"
      },
      {
        title: "Rechazada sol. ATR",
        value: statsValues.rechazadaSolAtr ?? "—",
        icon: FaBan,
        trend: null,
        color: "warning",
        to: "/contratos/ofertas?etapa=18"
      },
      {
        title: "Oferta Rechazada",
        value: statsValues.ofertaRechazada ?? "—",
        icon: FaTimesCircle,
        trend: null,
        color: "danger",
        to: "/contratos/ofertas?etapa=31"
      },
    ];

  const today = new Date().toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const formattedDate = today.charAt(0).toUpperCase() + today.slice(1);

  return (
    <Layout>
      <div className="bg-light min-vh-100 py-4">
        <Container fluid="lg">
          {/* Header */}
          <div className="dashboard-header animate-in mb-5">
            <h1 className="display-5 fw-bold text-primary mb-1">Hola, {user ? user.first_name : 'Agente'} 👋</h1>
            <p className="text-muted h5 fw-normal">{formattedDate}</p>
          </div>

          {/* Stats Row */}
          <div className="mb-5 animate-in delay-1">
            <Row className="g-4">
              {stats.map((stat, idx) => (
                <Col lg={3} md={6} key={idx}>
                  <Link to={stat.to} className="text-decoration-none d-block h-100">
                    <StatCard {...stat} />
                  </Link>
                </Col>
              ))}
            </Row>
          </div>

          {/* Quick Actions & Main Workflows */}
          <div className="mb-5 animate-in delay-2">
            <h4 className="fw-bold text-secondary mb-3">Acciones Rápidas</h4>
            <Row className="g-4">
              <Col lg={4} md={6}>
                <QuickActionCard
                  title="Nueva Oferta Luz"
                  description="Crear propuesta para suministro eléctrico"
                  icon={FaBolt}
                  to="/electrico/tarifas"
                  color="success"
                />
              </Col>
              <Col lg={4} md={6}>
                <QuickActionCard
                  title="Nueva Oferta Gas"
                  description="Crear propuesta para suministro de gas"
                  icon={FaFire}
                  to="/gas/tarifas"
                  color="warning"
                />
              </Col>
            </Row>
          </div>

          {/* Resources */}   
          <div className="animate-in delay-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="fw-bold text-secondary mb-0">Recursos Recientes</h4>
            </div>
            {loadingResources ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : resources.length === 0 ? (
              <div className="bg-white p-5 rounded-4 shadow-sm text-center text-muted">
                No hay recursos disponibles.
              </div>
            ) : (
              <Row className="g-4">
                {resources.map((resource) => {
                  const { icon: FileIcon, color: iconColor } = getFileIcon(resource.file);
                  const ext = getFileExtension(resource.file);
                  return (
                    <Col md={6} lg={4} key={resource.id}>
                      <a href={resource.file_url} target="_blank" rel="noopener noreferrer" className="text-decoration-none h-100">
                        <div className="d-flex align-items-center bg-white p-4 rounded-4 shadow-sm hover-elevate border-0 h-100 transition-all">
                          <div className={`me-3 p-3 rounded-circle bg-light ${iconColor}`}>
                            <FileIcon className="fs-3" />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <h6 className="mb-1 text-dark fw-bold text-truncate">{resource.title}</h6>
                            <div className="d-flex align-items-center gap-2">
                              <Badge bg="light" text="dark" className="border text-uppercase" style={{ fontSize: '0.65rem' }}>{ext || 'FILE'}</Badge>
                              <small className="text-muted text-truncate d-block">
                                {resource.category.charAt(0).toUpperCase() + resource.category.slice(1)}
                              </small>
                            </div>
                          </div>
                        </div>
                      </a>
                    </Col>
                  );
                })}
              </Row>
            )}
          </div>
        </Container>
      </div>
    </Layout>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.Auth.isAuthenticated,
  user: state.Auth.user,
  isSuperAdmin: state.Auth.isSuperAdmin,
});

export default connect(mapStateToProps)(Home);
